/**
 * ChunkMesher - Bridge between Chunk data and SurfaceNet algorithm
 * 
 * This module handles the game-specific logic of extracting voxel data
 * from Chunks and their neighbors, then passing it to the pure SurfaceNet algorithm.
 * 
 * OPTIMIZATION: Pre-expands chunk data with margins into a flat array,
 * then passes it directly to SurfaceNet for cache-efficient access.
 * 
 * Two paths:
 * - expandChunkToGrid(): Fills a grid buffer for worker dispatch (main thread only does expansion)
 * - meshChunk(): Sync fallback — expand + SurfaceNet in one call (used by remeshAllDirty, etc.)
 */

import {
  CHUNK_SIZE,
  GRID_SIZE,
  VOXELS_PER_CHUNK,
  LIGHT_MAX,
  chunkKeyLod,
  POSITIVE_MARGIN_OFFSETS_7,
  voxelIndex,
  packVoxel,
  mat,
} from '@worldify/shared';
import { Chunk } from './Chunk.js';
import { meshVoxelsSplit, SurfaceNetInput, SplitSurfaceNetOutput } from './SurfaceNet.js';

/**
 * Margin policy for an absent neighbour position — how the mesher fills margin voxels (and whether
 * a high face may be built) when the neighbour chunk isn't loaded:
 *   'air'     — genuine open sky (above the column's content top): fill the margin with AIR and
 *               MESH the face, so a terrain top at the boundary caps correctly.
 *   'solid'   — provably fully-solid terrain (below the column's lowest surface — coarse chunks
 *               resolve this from lodColumnInfo): fill with SOLID rock and MESH the face; there is
 *               no surface crossing there, so no geometry is invented.
 *   'unknown' — not loaded yet: extrapolate the edge and SKIP the face (never render such a mesh).
 * Registered by VoxelWorld. Main-thread only (expansion runs on the main thread).
 */
export type MarginPolicy = 'air' | 'solid' | 'unknown';
let marginPolicyFn: ((cx: number, cy: number, cz: number, lod: number) => MarginPolicy) | null = null;
export function setMarginPolicy(fn: (cx: number, cy: number, cz: number, lod: number) => MarginPolicy): void {
  marginPolicyFn = fn;
}

/** A virtual all-air chunk (weight below the surface, full sky light) used as the margin for an
 *  open-sky neighbour, so surfaces at that boundary mesh against air instead of extrapolated solid. */
const AIR_CHUNK = new Uint32Array(VOXELS_PER_CHUNK).fill(packVoxel(-0.5, 0, LIGHT_MAX));

/** A virtual all-solid chunk (deep-rock material, dark) used as the margin for a neighbour that is
 *  provably below the surface everywhere (coarse-column solid clip) — meshes flat against rock. */
const SOLID_CHUNK = new Uint32Array(VOXELS_PER_CHUNK).fill(packVoxel(0.5, mat('rock2'), 0));

/** The policy's margin fill for an absent neighbour, or null for 'unknown'. The one place the
 *  policy is consulted, so the seam's sign lattice reproduces exactly what the mesher saw. */
export function marginPolicyData(cx: number, cy: number, cz: number, lod: number): Uint32Array | null {
  switch (marginPolicyFn?.(cx, cy, cz, lod)) {
    case 'air': return AIR_CHUNK;
    case 'solid': return SOLID_CHUNK;
    default: return null;
  }
}

/** One margin voxel from an already policy-filled `src`; absent, it extrapolates the consumer's
 *  clamped edge voxel. `mx/my/mz` are grid coords with at least one >= CHUNK_SIZE. */
export function marginVoxelFrom(
  src: Uint32Array | null, dataArray: Uint32Array, mx: number, my: number, mz: number,
): number {
  const ox = mx >= CS, oy = my >= CS, oz = mz >= CS;
  if (src) return src[voxelIndex(ox ? mx - CS : mx, oy ? my - CS : my, oz ? mz - CS : mz)];
  return dataArray[voxelIndex(ox ? CS - 1 : mx, oy ? CS - 1 : my, oz ? CS - 1 : mz)];
}

// Re-export types for convenience
export type { SplitSurfaceNetOutput as ChunkMeshOutput };

// Derived grid constants
const GRID_SIZE_SQ = GRID_SIZE * GRID_SIZE;
const CS = CHUNK_SIZE;                       // 32
const CS_SQ = CS * CS;                       // 1024

// Reusable expanded grid buffer for sync path (avoids allocation per chunk)
let syncGrid: Uint32Array | null = null;

function getSyncGrid(): Uint32Array {
  if (!syncGrid) {
    syncGrid = new Uint32Array(GRID_SIZE * GRID_SIZE * GRID_SIZE);
  }
  return syncGrid;
}

/**
 * Expand chunk data with margins into a flat GRID_SIZE³ grid.
 * This allows direct index arithmetic in the SurfaceNet hot loop.
 * 
 * OPTIMIZED: Bulk-copies inner 32³ rows, then fills the high-side margin
 * (+X, +Y, +Z) by looking up the 7 neighbor data arrays once (3 face,
 * 3 edge, 1 corner) and reading directly via voxelIndex. Avoids per-voxel
 * chunkKey string allocation and Map lookups (~6,500 → 7).
 */
function expandChunkData(
  chunk: Chunk,
  neighbors: Map<string, Chunk>,
  useTemp: boolean,
  grid: Uint32Array
): void {
  const dataArray = (useTemp && chunk.tempData) ? chunk.tempData : chunk.data;
  
  // === Phase 1: Bulk-copy inner 32³ block (rows via subarray) ===
  for (let z = 0; z < CS; ++z) {
    const gridZBase = z * GRID_SIZE_SQ;
    const dataZBase = z * CS_SQ;
    for (let y = 0; y < CS; ++y) {
      const gridRowStart = gridZBase + y * GRID_SIZE;
      const dataRowStart = dataZBase + y * CS;
      grid.set(dataArray.subarray(dataRowStart, dataRowStart + CS), gridRowStart);
    }
  }
  
  // === Phase 2: Look up the 7 high-side neighbor data arrays once ===
  // Neighbour keys carry the chunk's own LOD — margins are SAME-LOD only, so a coarse chunk can
  // never read a fine chunk (or vice versa) as its margin.
  const cx = chunk.cx, cy = chunk.cy, cz = chunk.cz, lod = chunk.lod;
  const getNeighborData = (ncx: number, ncy: number, ncz: number): Uint32Array | null => {
    const n = neighbors.get(chunkKeyLod(ncx, ncy, ncz, lod));
    if (n) return (useTemp && n.tempData) ? n.tempData : n.data;
    // Not loaded: the margin policy resolves provable cases (open sky → AIR, below-surface →
    // SOLID) so the face still meshes; 'unknown' returns null → the caller extrapolates the edge
    // (avoids inventing surface) and the face is skipped.
    return marginPolicyData(ncx, ncy, ncz, lod);
  };

  // Face neighbors
  const nPX  = getNeighborData(cx + 1, cy,     cz);     // +X face
  const nPY  = getNeighborData(cx,     cy + 1, cz);     // +Y face
  const nPZ  = getNeighborData(cx,     cy,     cz + 1); // +Z face
  // Edge neighbors (pair of high axes)
  const nPXY = getNeighborData(cx + 1, cy + 1, cz);     // +X+Y edge
  const nPXZ = getNeighborData(cx + 1, cy,     cz + 1); // +X+Z edge
  const nPYZ = getNeighborData(cx,     cy + 1, cz + 1); // +Y+Z edge
  // Corner neighbor
  const nPXYZ = getNeighborData(cx + 1, cy + 1, cz + 1); // +X+Y+Z corner

  // Helper: read a margin voxel by determining which neighbor owns it.
  // mx, my, mz are in the range [0..33] with at least one ≥ 32.
  // Each overflowed axis maps to the neighbor; non-overflowed axes use the local coord.
  const readMarginVoxel = (mx: number, my: number, mz: number): number => {
    const ox = mx >= CS;  // overflows into +X neighbor?
    const oy = my >= CS;  // overflows into +Y neighbor?
    const oz = mz >= CS;  // overflows into +Z neighbor?

    let src: Uint32Array | null;
    if (ox && oy && oz) {
      src = nPXYZ;
    } else if (ox && oy) {
      src = nPXY;
    } else if (ox && oz) {
      src = nPXZ;
    } else if (oy && oz) {
      src = nPYZ;
    } else if (ox) {
      src = nPX;
    } else if (oy) {
      src = nPY;
    } else {
      src = nPZ; // oz must be true
    }

    return marginVoxelFrom(src, dataArray, mx, my, mz);
  };
  
  // === Phase 2a: Fill z=32..33 slabs (full xy planes) ===
  for (let z = CS; z < GRID_SIZE; ++z) {
    const gridZBase = z * GRID_SIZE_SQ;
    // Inner part where x<32 and y<32 — reads from +Z face neighbor only
    for (let y = 0; y < CS; ++y) {
      const gridRowBase = gridZBase + y * GRID_SIZE;
      if (nPZ) {
        const nz = z - CS;
        const srcStart = voxelIndex(0, y, nz);
        grid.set(nPZ.subarray(srcStart, srcStart + CS), gridRowBase);
      } else {
        // Extrapolate: repeat z=31 row from this chunk
        const srcStart = voxelIndex(0, y, CS - 1);
        grid.set(dataArray.subarray(srcStart, srcStart + CS), gridRowBase);
      }
      // x=32..33 in this row (y<32, z>=32) — edge/corner voxels
      for (let x = CS; x < GRID_SIZE; ++x) {
        grid[gridRowBase + x] = readMarginVoxel(x, y, z);
      }
    }
    // y=32..33 rows in this z slab
    for (let y = CS; y < GRID_SIZE; ++y) {
      const gridRowBase = gridZBase + y * GRID_SIZE;
      for (let x = 0; x < GRID_SIZE; ++x) {
        grid[gridRowBase + x] = readMarginVoxel(x, y, z);
      }
    }
  }
  
  // === Phase 2b: Fill y=32..33 rows (only z=0..31) ===
  for (let z = 0; z < CS; ++z) {
    const gridZBase = z * GRID_SIZE_SQ;
    for (let y = CS; y < GRID_SIZE; ++y) {
      const gridRowBase = gridZBase + y * GRID_SIZE;
      // Inner x=0..31 — reads from +Y face neighbor only
      if (nPY) {
        const ny = y - CS;
        const srcStart = voxelIndex(0, ny, z);
        grid.set(nPY.subarray(srcStart, srcStart + CS), gridRowBase);
      } else {
        // Extrapolate: repeat y=31 row from this chunk
        const srcStart = voxelIndex(0, CS - 1, z);
        grid.set(dataArray.subarray(srcStart, srcStart + CS), gridRowBase);
      }
      // x=32..33 columns (y>=32, z<32) — edge voxels
      for (let x = CS; x < GRID_SIZE; ++x) {
        grid[gridRowBase + x] = readMarginVoxel(x, y, z);
      }
    }
  }
  
  // === Phase 2c: Fill x=32..33 columns (only y=0..31, z=0..31) ===
  for (let z = 0; z < CS; ++z) {
    const gridZBase = z * GRID_SIZE_SQ;
    for (let y = 0; y < CS; ++y) {
      const gridRowBase = gridZBase + y * GRID_SIZE;
      // Only 2 voxels per row — reads from +X face neighbor only
      if (nPX) {
        for (let x = CS; x < GRID_SIZE; ++x) {
          grid[gridRowBase + x] = nPX[voxelIndex(x - CS, y, z)];
        }
      } else {
        // Extrapolate: repeat x=31 voxel from this chunk
        const val = dataArray[voxelIndex(CS - 1, y, z)];
        for (let x = CS; x < GRID_SIZE; ++x) {
          grid[gridRowBase + x] = val;
        }
      }
    }
  }
}

/**
 * Compute skipHighBoundary flags for a chunk.
 */
export function getSkipHighBoundary(
  chunk: Chunk,
  neighbors: Map<string, Chunk>,
): [boolean, boolean, boolean] {
  // Skip a high face only when its neighbour is truly absent (not loaded yet) — NOT when the margin
  // policy resolves it (open sky / solid ground). Policy-resolved faces mesh against AIR/SOLID
  // (see getNeighborData), so skipping them would re-open the gap.
  return [
    highFaceAbsent(chunk.cx + 1, chunk.cy, chunk.cz, chunk.lod, neighbors),
    highFaceAbsent(chunk.cx, chunk.cy + 1, chunk.cz, chunk.lod, neighbors),
    highFaceAbsent(chunk.cx, chunk.cy, chunk.cz + 1, chunk.lod, neighbors),
  ];
}

/**
 * WHICH high-side margin sources this mesh had to EXTRAPOLATE, as a bitmask over
 * `POSITIVE_MARGIN_OFFSETS_7` (bit i = that offset was absent, so readMarginVoxel clamped to this
 * chunk's own edge voxel instead of reading the neighbour).
 *
 * The 3 FACE bits are the same information as `getSkipHighBoundary` — a skipped face never draws,
 * so the mesh is marked MeshedIncomplete and the existing healing paths chase it. The 4 DIAGONAL
 * bits (+XY, +XZ, +YZ, +XYZ) are the gap this closes: a diagonal source feeds a one-vertex-wide
 * EDGE or CORNER column of the grid, the mesh built from an extrapolated value there is
 * geometrically WRONG but topologically complete, and it was therefore labelled MeshedComplete and
 * drawn. Every re-mesh trigger and every healer gated on `phase === MeshedIncomplete`, so a
 * diagonal arriving after its consumer meshed never corrected anything: permanent hairline cracks
 * along chunk edges and pinholes at corners, seeded wherever streaming order happened to bring a
 * diagonal last. Recording the mask lets the arrival re-trigger fire on exactly the consumers that
 * extrapolated the arriving offset, and nothing else (an unconditional diagonal re-trigger is the
 * dispatch churn this pipeline deliberately removed).
 */
export function getExtrapolatedMargins(chunk: Chunk, neighbors: Map<string, Chunk>): number {
  let mask = 0;
  for (let i = 0; i < POSITIVE_MARGIN_OFFSETS_7.length; i++) {
    const [dx, dy, dz] = POSITIVE_MARGIN_OFFSETS_7[i];
    if (highFaceAbsent(chunk.cx + dx, chunk.cy + dy, chunk.cz + dz, chunk.lod, neighbors)) {
      mask |= 1 << i;
    }
  }
  return mask;
}

/** A high-side neighbour is "absent" for boundary-skipping if it's neither loaded nor policy-resolved. */
function highFaceAbsent(cx: number, cy: number, cz: number, lod: number, neighbors: Map<string, Chunk>): boolean {
  if (neighbors.has(chunkKeyLod(cx, cy, cz, lod))) return false;      // loaded
  const policy = marginPolicyFn?.(cx, cy, cz, lod);
  if (policy === 'air' || policy === 'solid') return false;           // resolved → meshed, not skipped
  return true;                                                        // genuinely not loaded yet
}

/**
 * Expand chunk data into a provided grid buffer for worker dispatch.
 * Main thread fills the grid, then transfers it to a worker for SurfaceNet.
 * 
 * @param chunk The chunk to expand
 * @param neighbors Map of neighbor chunks for margin sampling
 * @param grid Grid buffer to fill (from MeshWorkerPool.takeGrid())
 * @param useTemp If true, use tempData for preview rendering
 * @returns skipHighBoundary flags for the worker
 */
export function expandChunkToGrid(
  chunk: Chunk,
  neighbors: Map<string, Chunk>,
  grid: Uint32Array,
  useTemp: boolean = false,
): [boolean, boolean, boolean] {
  expandChunkData(chunk, neighbors, useTemp, grid);
  return getSkipHighBoundary(chunk, neighbors);
}

/**
 * Generate meshes for a chunk synchronously (expand + SurfaceNet in one call).
 * Used as fallback by remeshAllDirty() and other sync paths.
 * 
 * @param chunk The chunk to mesh
 * @param neighbors Map of neighbor chunks for margin sampling
 * @param useTemp If true, use tempData for preview rendering (defaults to false)
 * @returns Separate mesh outputs for solid and transparent materials
 */
export function meshChunk(
  chunk: Chunk,
  neighbors: Map<string, Chunk>,
  useTemp: boolean = false
): SplitSurfaceNetOutput {
  const grid = getSyncGrid();
  expandChunkData(chunk, neighbors, useTemp, grid);
  const skipHighBoundary = getSkipHighBoundary(chunk, neighbors);

  const input: SurfaceNetInput = { 
    dims: [GRID_SIZE, GRID_SIZE, GRID_SIZE], 
    data: grid,
    skipHighBoundary,
    lod: chunk.lod,
  };
  return meshVoxelsSplit(input);
}
